import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { useAuth } from "../App";
import { toast } from "sonner";
import {
  ArrowLeft, Building, Calendar, Clock, CheckCircle, AlertTriangle,
  Users, FileText, Camera, Package, PlusCircle, ChevronRight,
  TrendingUp, DollarSign, Hammer, BookOpen
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STAGE_STATUS = {
  pending: { label: "Pendente", color: "bg-neutral-100 text-neutral-600" },
  in_progress: { label: "Em andamento", color: "bg-blue-100 text-blue-700" },
  completed: { label: "Concluído", color: "bg-amber-100 text-amber-700" },
  approved: { label: "Aprovado", color: "bg-emerald-100 text-emerald-700" },
  approved_with_reservations: { label: "Aprovado c/ ressalvas", color: "bg-amber-100 text-amber-700" },
  rejected: { label: "Reprovado", color: "bg-red-100 text-red-700" }
};

export default function ProjectPage() {
  const { projectId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const res = await fetch(`${API_URL}/api/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setProject(await res.json());
      } else {
        toast.error("Projeto não encontrado");
        navigate("/client");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen-mobile flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-neutral-200 border-t-neutral-900 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!project) return null;

  const progress = project.progress || { percentage: 0, completed_stages: 0, total_stages: 0 };

  return (
    <div className="min-h-screen-mobile bg-white pb-8" data-testid="project-page">
      {/* Header */}
      <header className="sticky-header border-b border-neutral-100">
        <div className="px-5 h-14 flex items-center gap-4">
          <button 
            onClick={() => navigate("/client")}
            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-neutral-50 touch-target"
          >
            <ArrowLeft className="w-5 h-5 text-neutral-700" />
          </button>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-neutral-900 truncate">{project.name}</p>
            <p className="text-xs text-neutral-500">Minha Obra</p>
          </div>
        </div>
      </header>

      {/* Progress Banner */}
      <div className="px-5 py-6 bg-neutral-900 text-white">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-sm text-neutral-400">Progresso geral</p>
            <p className="text-3xl font-semibold">{progress.percentage}%</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-neutral-400">Etapas</p>
            <p className="text-lg font-medium">{progress.completed_stages}/{progress.total_stages}</p>
          </div>
        </div>
        <div className="h-2 bg-neutral-700 rounded-full overflow-hidden">
          <div 
            className="h-full bg-emerald-500 rounded-full transition-all duration-500"
            style={{ width: `${progress.percentage}%` }}
          ></div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="px-5 py-3 border-b border-neutral-100">
        <div className="flex gap-2 overflow-x-auto scrollbar-hide">
          {[
            { id: "overview", label: "Visão Geral", icon: TrendingUp },
            { id: "stages", label: "Cronograma", icon: Calendar },
            { id: "diary", label: "Diário", icon: BookOpen },
            { id: "materials", label: "Materiais", icon: Package },
            { id: "providers", label: "Prestadores", icon: Users }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id ? "bg-neutral-900 text-white" : "bg-neutral-100 text-neutral-600"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <main className="px-5 py-6">
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-6 animate-up">
            {/* Stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="card-subtle p-4">
                <DollarSign className="w-5 h-5 text-neutral-500 mb-2" />
                <p className="text-2xl font-semibold text-neutral-900">
                  R$ {(project.total_budget / 1000).toFixed(0)}k
                </p>
                <p className="text-sm text-neutral-500">Orçamento total</p>
              </div>
              <div className="card-subtle p-4">
                <DollarSign className="w-5 h-5 text-emerald-500 mb-2" />
                <p className="text-2xl font-semibold text-emerald-600">
                  R$ {(project.total_spent / 1000).toFixed(0)}k
                </p>
                <p className="text-sm text-neutral-500">Gasto até agora</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="space-y-3">
              <h3 className="font-medium text-neutral-900">Ações rápidas</h3>
              <div className="grid grid-cols-2 gap-3">
                <button 
                  onClick={() => setActiveTab("diary")}
                  className="card-clean p-4 text-left"
                >
                  <Camera className="w-5 h-5 text-neutral-500 mb-2" />
                  <p className="font-medium text-neutral-900 text-sm">Diário de obra</p>
                  <p className="text-xs text-neutral-500">{project.diary_entries?.length || 0} registros</p>
                </button>
                <button 
                  onClick={() => navigate(`/project/${projectId}/addendum`)}
                  className="card-clean p-4 text-left"
                >
                  <FileText className="w-5 h-5 text-neutral-500 mb-2" />
                  <p className="font-medium text-neutral-900 text-sm">Aditivos</p>
                  <p className="text-xs text-neutral-500">{project.addendums?.length || 0} solicitações</p>
                </button>
              </div>
            </div>

            {/* Recent Diary */}
            {project.diary_entries?.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-neutral-900">Diário recente</h3>
                  <button onClick={() => setActiveTab("diary")} className="text-sm text-neutral-500">
                    Ver todos
                  </button>
                </div>
                {project.diary_entries.slice(0, 2).map(entry => (
                  <div key={entry.entry_id} className="card-subtle p-4">
                    <div className="flex items-start gap-3">
                      {entry.photos?.[0] ? (
                        <img src={entry.photos[0]} alt="" className="w-16 h-16 rounded-lg object-cover" />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-neutral-200 flex items-center justify-center">
                          <Camera className="w-6 h-6 text-neutral-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-neutral-900 line-clamp-2">{entry.progress_text}</p>
                        <p className="text-xs text-neutral-500 mt-1">
                          {new Date(entry.date).toLocaleDateString("pt-BR")}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Bonus Info */}
            {project.early_delivery_bonus && (
              <div className="card-clean p-4 border-emerald-200 bg-emerald-50">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium text-emerald-900">Bônus por entrega antecipada</p>
                    <p className="text-sm text-emerald-700 mt-0.5">
                      {project.early_delivery_bonus.bonus_type === "percentage" 
                        ? `${(project.early_delivery_bonus.percentage * 100).toFixed(1)}% do valor`
                        : `R$ ${project.early_delivery_bonus.fixed_value}`}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Stages Tab */}
        {activeTab === "stages" && (
          <div className="space-y-4 animate-up">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-neutral-900">Cronograma macro</h3>
              <span className="text-sm text-neutral-500">{project.stages?.length || 0} etapas</span>
            </div>
            
            {project.stages?.map((stage, index) => {
              const status = STAGE_STATUS[stage.status] || STAGE_STATUS.pending;
              return (
                <button
                  key={stage.stage_id}
                  onClick={() => navigate(`/project/${projectId}/stage/${stage.stage_id}`)}
                  className="list-item w-full"
                >
                  <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center text-sm font-semibold text-neutral-600">
                    {stage.order}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-neutral-900">{stage.name}</p>
                    {stage.estimated_cost > 0 && (
                      <p className="text-sm text-neutral-500">
                        R$ {stage.estimated_cost.toLocaleString("pt-BR")}
                      </p>
                    )}
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                    {status.label}
                  </span>
                  <ChevronRight className="w-5 h-5 text-neutral-300" />
                </button>
              );
            })}
          </div>
        )}

        {/* Diary Tab */}
        {activeTab === "diary" && (
          <div className="space-y-4 animate-up">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-neutral-900">Diário de obra</h3>
              <Button 
                onClick={() => navigate(`/project/${projectId}/diary/new`)}
                className="btn-minimal btn-primary h-9 px-4 text-sm"
              >
                <PlusCircle className="w-4 h-4 mr-1" />
                Novo registro
              </Button>
            </div>

            {project.diary_entries?.length === 0 ? (
              <div className="card-subtle p-8 text-center">
                <BookOpen className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
                <p className="text-neutral-500">Nenhum registro no diário</p>
              </div>
            ) : (
              project.diary_entries?.map(entry => (
                <div key={entry.entry_id} className="card-clean p-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-neutral-900">
                      {new Date(entry.date).toLocaleDateString("pt-BR", { 
                        weekday: 'long', 
                        day: 'numeric', 
                        month: 'short' 
                      })}
                    </p>
                    {entry.client_approved && (
                      <span className="badge-success text-xs">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Aprovado
                      </span>
                    )}
                  </div>
                  
                  {entry.photos?.length > 0 && (
                    <div className="flex gap-2 mb-3 overflow-x-auto scrollbar-hide">
                      {entry.photos.map((photo, i) => (
                        <img key={i} src={photo} alt="" className="w-20 h-20 rounded-lg object-cover flex-shrink-0" />
                      ))}
                    </div>
                  )}
                  
                  <p className="text-sm text-neutral-700">{entry.progress_text}</p>
                  
                  {entry.difficulties && (
                    <div className="mt-2 flex items-center gap-2 text-amber-600 text-sm">
                      <AlertTriangle className="w-4 h-4" />
                      {entry.difficulties}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Materials Tab */}
        {activeTab === "materials" && (
          <div className="space-y-4 animate-up">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-neutral-900">Materiais</h3>
              <Button 
                onClick={() => navigate(`/project/${projectId}/materials/add`)}
                className="btn-minimal btn-primary h-9 px-4 text-sm"
              >
                <PlusCircle className="w-4 h-4 mr-1" />
                Adicionar
              </Button>
            </div>

            {/* Material Stats */}
            <div className="grid grid-cols-4 gap-2 text-center">
              {[
                { label: "Pendente", count: project.materials?.filter(m => m.status === "pending").length || 0 },
                { label: "Comprado", count: project.materials?.filter(m => m.status === "purchased").length || 0 },
                { label: "Entregue", count: project.materials?.filter(m => m.status === "delivered").length || 0 },
                { label: "Usado", count: project.materials?.filter(m => m.status === "used").length || 0 }
              ].map(stat => (
                <div key={stat.label} className="card-subtle p-3">
                  <p className="text-xl font-semibold text-neutral-900">{stat.count}</p>
                  <p className="text-xs text-neutral-500">{stat.label}</p>
                </div>
              ))}
            </div>

            {project.materials?.length === 0 ? (
              <div className="card-subtle p-8 text-center">
                <Package className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
                <p className="text-neutral-500">Nenhum material cadastrado</p>
              </div>
            ) : (
              project.materials?.map(material => (
                <div key={material.item_id} className="list-item">
                  <div className="w-10 h-10 rounded-lg bg-neutral-100 flex items-center justify-center">
                    <Package className="w-5 h-5 text-neutral-500" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-neutral-900">{material.name}</p>
                    <p className="text-sm text-neutral-500">
                      {material.quantity} {material.unit} • R$ {material.estimated_price?.toLocaleString("pt-BR")}
                    </p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    material.status === "pending" ? "bg-neutral-100 text-neutral-600" :
                    material.status === "purchased" ? "bg-blue-100 text-blue-700" :
                    material.status === "delivered" ? "bg-amber-100 text-amber-700" :
                    "bg-emerald-100 text-emerald-700"
                  }`}>
                    {material.status === "pending" ? "Pendente" :
                     material.status === "purchased" ? "Comprado" :
                     material.status === "delivered" ? "Entregue" : "Usado"}
                  </span>
                </div>
              ))
            )}
          </div>
        )}

        {/* Providers Tab */}
        {activeTab === "providers" && (
          <div className="space-y-4 animate-up">
            <div className="flex items-center justify-between">
              <h3 className="font-medium text-neutral-900">Prestadores</h3>
              <Button 
                onClick={() => navigate(`/project/${projectId}/providers/add`)}
                className="btn-minimal btn-secondary h-9 px-4 text-sm"
              >
                <PlusCircle className="w-4 h-4 mr-1" />
                Adicionar
              </Button>
            </div>

            {project.providers?.length === 0 ? (
              <div className="card-subtle p-8 text-center">
                <Users className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
                <p className="text-neutral-500">Nenhum prestador vinculado</p>
                <Button 
                  onClick={() => navigate(`/project/${projectId}/providers/add`)}
                  className="btn-minimal btn-primary mt-4"
                >
                  Adicionar prestador
                </Button>
              </div>
            ) : (
              project.providers?.map((provider, index) => (
                <div key={index} className="list-item">
                  <div className="w-10 h-10 rounded-full bg-neutral-200 flex items-center justify-center">
                    <Hammer className="w-5 h-5 text-neutral-500" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-neutral-900">{provider.role || "Prestador"}</p>
                    <p className="text-sm text-neutral-500">ID: {provider.provider_id?.slice(-8)}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-neutral-300" />
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
}
